import React, { useEffect, useState } from 'react';

import { bemCssModules } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { useDebounce } from '@utils/hooks';

import { useStore } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { withdrawActions } from '@modules/withdraw';

import Input from '@components/Input';
import Preloader from '@components/Preloader';

import UdpIcon from './udp.svg';

import style from './AddressInput.scss';

const cn = bemCssModules(style, 'AddressInput');

export type AddressInputType = {
  currency: CryptoCurrencyEnum;
  isLoading: boolean;
};

export const AddressInput: CustomFC<AddressInputType> = ({ className, currency, isLoading }) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [changeLoading, setChangeLoading] = useState(false);
  const [{ withdraw }, dispatch] = useStore();
  const debounced = useDebounce(CONST.inputDebounceTime);
  const { addressLoading, errors, address: withdrawAddress } = withdraw;

  const validationError = !withdrawAddress[currency]?.isValid && t('withdraw.invalid_address');
  const errorAddress =
    address && !addressLoading && !changeLoading && (errors.data?.address || validationError);

  useEffect(() => {
    setAddress(withdrawAddress[currency]?.domain || withdrawAddress[currency]?.address || '');
  }, [currency]);

  const reset = () => {
    dispatch(withdrawActions.setAddressValidation({ isValid: false }));
    dispatch(withdrawActions.setAddressLoading(true));
    setChangeLoading(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    const { value } = event.target;

    setAddress(value);
    debounced(() => {
      dispatch(withdrawActions.setAddress({ address: value, currency }));
      setChangeLoading(false);
    });
  };

  return (
    <div className={cn(null, [className])}>
      <Input
        className={cn('wallet-input')}
        autoComplete="new-password"
        onChange={handleChange}
        value={address}
        error={errorAddress}
      />
      {withdrawAddress[currency]?.address && (
        <Preloader
          className={cn('loader')}
          classNames={{
            circle: cn('circle', { hidden: !isLoading && !changeLoading }),
            svg: cn('svg', { hidden: !isLoading && !changeLoading }),
          }}
          size={12}
        />
      )}
      {!address && (
        <div className={cn('placeholder')}>
          {`${currency} ${t('operations.address')}`}
          <span className={cn('placeholder-icon')}>
            <UdpIcon />
          </span>
        </div>
      )}
    </div>
  );
};

export default AddressInput;
